import React from 'react';
import { useTranslation } from 'react-i18next';
import { useField } from 'formik';
import { KoobInputNumber } from '@koob/ui';
import { formatCurrency } from 'utils';

export default function AccommodationPriceCell({
  roomIndex,
  priceIndex,
  categoryFieldName,
  isBaseCell,
  basePrice,
  paxCount = 1,
  isReadOnly = false,
}) {
  const { t } = useTranslation('experiences');
  // Use priceIndex if provided (period-aware), fall back to roomIndex (legacy)
  const idx = priceIndex != null ? priceIndex : roomIndex;
  const priceName = `${categoryFieldName}.prices.${idx}.supplement`;
  const typeName = `${categoryFieldName}.prices.${idx}.supplementType`;
  const [priceField, , priceHelpers] = useField(priceName);
  const [typeField, , typeHelpers] = useField(typeName);
  const [{ value: currency }] = useField('currency');

  const isEnabled = typeField.value !== 'disabled';

  const toggleEnabled = () => {
    if (isEnabled) {
      typeHelpers.setValue('disabled');
      priceHelpers.setValue(0);
    } else {
      typeHelpers.setValue('value');
    }
  };

  // Base cell: show the period base price, greyed out, not editable
  if (isBaseCell) {
    return (
      <div className="flex items-center gap-2">
        <input
          type="checkbox"
          checked={true}
          disabled
          className="w-4 h-4 rounded border-gray-200 text-gray-300"
        />
        <div className="w-24 rounded-md border border-gray-200 bg-gray-100 px-2.5 py-1.5 text-sm text-gray-500 cursor-not-allowed">
          {formatCurrency({
            amount: basePrice ?? 0,
            currency,
          })}
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2">
      <input
        type="checkbox"
        checked={isEnabled}
        onChange={toggleEnabled}
        disabled={isReadOnly}
        className="w-4 h-4 rounded border-gray-300 text-orange-500 focus:ring-orange-400"
        title={
          isEnabled
            ? t('accommodation.disableRoom', 'Disable this combination')
            : t('accommodation.enableRoom', 'Enable this combination')
        }
      />
      {isEnabled ? (
        <div className="flex items-center gap-1">
          <input
            type="text"
            disabled={true}
            className="w-24 rounded-md border border-gray-300 px-2.5 py-1.5 text-sm focus:border-orange-400 focus:ring-1 focus:ring-orange-400 focus:outline-none transition disabled:!bg-gray-200 disabled:!text-gray-500 disabled:!border-gray-200 disabled:!cursor-not-allowed disabled:!opacity-100"
            value={basePrice ?? '—'}
          />

          <span className="text-xs text-gray-400">+</span>

          <div className="w-24">
            <KoobInputNumber
              size="sm"
              min={0}
              value={priceField.value}
              emptyValue={0}
              onValueChange={(value) => priceHelpers.setValue(value ?? 0)}
              disabled={isReadOnly}
            />
          </div>
          {basePrice != null && priceField.value >= 0 && (
            <span className="text-xs text-gray-400 ml-1">
              ={' '}
              {formatCurrency({
                amount: (basePrice + (priceField.value ?? 0)) * paxCount,
                currency,
              })}
            </span>
          )}
        </div>
      ) : (
        <span className="text-sm text-gray-300 italic">
          {t('accommodation.notAvailable', 'N/A')}
        </span>
      )}
    </div>
  );
}
